import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithInfiniteQuery, atomWithQuery } from 'jotai-tanstack-query';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { invalidateQueries } from '@/lib/query-client';
import { isAuthenticatedAtom } from './use-auth';
import {
	automationReplyService,
	type AutomationReplyFilter,
	type AutomationReply,
	type AutomationReplyPage,
	type AutomationReviewVerdict
} from '@/services/automation-reply.service';

const QUERY_ROOT = 'automation-replies';

const repliesQueryAtom = (filter: AutomationReplyFilter) =>
	atomWithInfiniteQuery((get) => ({
		// The brand is in the key: two brands never read each other's log.
		queryKey: [QUERY_ROOT, buScopeKey(get), filter],
		queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
			automationReplyService.fetchPage(filter, pageParam, buScopeParams(get)),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage: AutomationReplyPage) =>
			lastPage.hasMore ? lastPage.entries.at(-1)?.id : undefined,
		enabled: get(isAuthenticatedAtom)
	}));

/** The selected brand's automated replies, newest first, a page at a time. */
export function useAutomationReplies(filter: AutomationReplyFilter) {
	// `filter` is memoised by the caller, so a new atom (and query) only when it really changes.
	const queryAtom = useMemo(() => repliesQueryAtom(filter), [filter]);
	const { data, isLoading, isError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } = useAtomValue(queryAtom);
	const entries = useMemo(() => data?.pages.flatMap((page) => page.entries) ?? [], [data]);
	const loadMore = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);
	const total = data?.pages[0]?.total;
	return { entries, total, isLoading, isError, refetch, hasMore: Boolean(hasNextPage), loadingMore: isFetchingNextPage, loadMore };
}

const intentsQueryAtom = atomWithQuery((get) => ({
	queryKey: [QUERY_ROOT, 'intents', buScopeKey(get)],
	queryFn: () => automationReplyService.fetchIntents(buScopeParams(get)),
	enabled: get(isAuthenticatedAtom)
}));

/** The intents the selected brand's log names, for its intent filter. */
export function useAutomationReplyIntents() {
	return useAtomValue(intentsQueryAtom).data ?? [];
}

const replyQueryAtom = (id: string) =>
	atomWithQuery((get) => ({
		queryKey: [QUERY_ROOT, 'entry', id],
		queryFn: () => automationReplyService.fetchOne(id),
		enabled: get(isAuthenticatedAtom) && Boolean(id),
		retry: false
	}));

/** One entry by id, for a link from the Slack alert. */
export function useAutomationReply(id: string | null) {
	const queryAtom = useMemo(() => replyQueryAtom(id ?? ''), [id]);
	return useAtomValue(queryAtom);
}

const threadQueryAtom = (id: string) =>
	atomWithQuery((get) => ({
		queryKey: [QUERY_ROOT, 'thread', id],
		queryFn: () => automationReplyService.fetchThread(id),
		enabled: get(isAuthenticatedAtom) && Boolean(id),
		retry: false
	}));

/** The ticket's conversation behind one reply, for the reviewer. */
export function useAutomationReplyThread(id: string | null) {
	const queryAtom = useMemo(() => threadQueryAtom(id ?? ''), [id]);
	return useAtomValue(queryAtom);
}

/** Runs the automation checks over the brand's replies in the range, then refreshes the log to show them. */
export async function runAutomationChecks(
	filter: Pick<AutomationReplyFilter, 'from' | 'to' | 'intent' | 'outcome'>,
	scope: { bu_id?: string }
) {
	try {
		return await automationReplyService.runChecks(filter, scope);
	} finally {
		void invalidateQueries([QUERY_ROOT]);
	}
}

/** Checks one reply again, then refreshes the log. */
export async function rerunAutomationChecks(id: string): Promise<AutomationReply> {
	try {
		return await automationReplyService.rerunChecks(id);
	} finally {
		void invalidateQueries([QUERY_ROOT]);
	}
}

/** Records a Sentinel's verdict, then refreshes every list so the reply leaves the queue. */
export async function reviewAutomationReply(
	id: string,
	review: { verdict: AutomationReviewVerdict; reasons: string[]; note: string | null }
) {
	try {
		return await automationReplyService.review(id, review);
	} finally {
		// Also after a 409: someone else reviewed it, so it has left the queue either way.
		void invalidateQueries([QUERY_ROOT]);
	}
}
